import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { FixedPageDocument } from '@/graphql/contentful/api';
import type {
  ContentfulFixedPageCMSData,
  SectionsOpts,
} from '@/integrations/contentful';
import {
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { paths } from '@/lib/paths';
import { commonMessages } from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { sortNewsByPublishDateDesc } from '@/views/NewsView/utils/sort';

import ArticlePreview from './components/Article/ArticlePreview';
import PinnedNews from './components/PinnedNews/PinnedNews';
import TagsFilter from './components/Tags/TagsFilter';
import type { NewsPageType } from './types';

import * as S from './News.styles';

interface NewsProps {
  cms?: ContentfulFixedPageCMSData;
  newsPagesCollection?: Array<NewsPageType | null> | undefined;
}

export const News = ({ cms, newsPagesCollection }: NewsProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const { formatMessage } = useIntl();

  const pinnedNews = liveCms?.pinnedNews;
  const sortedNewsPages = sortNewsByPublishDateDesc(
    (newsPagesCollection || []).filter(
      (item): item is NewsPageType => item !== null,
    ),
  ).filter(
    (item) => !pinnedNews?.find((pinnedNew) => pinnedNew.slug === item.slug),
  );

  const allNewsTag = formatMessage(commonMessages.newsTags);
  const [selectedTag, setSelectedTag] = useState<string>(allNewsTag);
  const [filteredArticles, setFilteredArticles] = useState(sortedNewsPages);
  const latestArticlesRef = useRef<HTMLDivElement>(null);

  const { headerRef } = useConfigProvider();

  const handleTagClick = (tag: string) => {
    // Scroll to the latest articles
    if (latestArticlesRef.current) {
      const headerHeight =
        headerRef?.current?.getBoundingClientRect().height ?? 0;
      const sectionTop =
        latestArticlesRef.current.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: sectionTop - headerHeight,
        behavior: 'smooth',
      });
    }
    setSelectedTag(tag);
    if (tag === allNewsTag) {
      setFilteredArticles(sortedNewsPages);
    } else {
      const filtered = sortedNewsPages.filter((item) =>
        item?.tagsCollection?.items.some((t) => t?.value === tag),
      );
      setFilteredArticles(filtered);
    }
  };

  const cmsSectionOpts: SectionsOpts = [
    {
      type: 'Breadcrumb',
      opts: {
        items: [
          {
            label: formatMessage(commonMessages.home),
            target: paths.url(),
          },
          {
            label: formatMessage(commonMessages.news),
            target: paths.news.url(),
          },
        ],
      },
    },
  ];

  return (
    <>
      {liveCms?.topContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(
            data,
            cmsSectionOpts,
            liveCms.cmsExtraData,
          )}
        </Fragment>
      ))}
      <TagsFilter
        newsPagesCollection={newsPagesCollection || []}
        onTagClick={handleTagClick}
        selectedTag={selectedTag}
      />
      <S.Wrapper>
        {pinnedNews && pinnedNews.length > 0 && (
          <PinnedNews pinnedNews={pinnedNews} />
        )}

        {filteredArticles && (
          <S.Container ref={latestArticlesRef}>
            <S.TextContainer>
              {formatMessage(commonMessages.latestArticles)}
            </S.TextContainer>
            <S.StyledMasonry
              breakpointCols={{
                default: 3,
                1100: 2,
                700: 1,
              }}
              className='masonry-grid'
              columnClassName='masonry-grid_column'
            >
              {filteredArticles.map((item) => (
                <ArticlePreview key={item?.sys?.id} data={item} />
              ))}
            </S.StyledMasonry>
          </S.Container>
        )}
      </S.Wrapper>
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(data, undefined, liveCms.cmsExtraData)}
        </Fragment>
      ))}
    </>
  );
};

News.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
